import React, { useMemo } from "react";
import { Box, Typography, Grid, Paper, Tooltip } from "@mui/material";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday, isSameDay } from "date-fns";
import { enUS } from "date-fns/locale";

import { colors } from "../ui/colors";
import { ItemWeek } from "./weekJobs";
import { useWidgetVariant } from "./useWidgetVariant";
import { jobOverlapsDay } from "./utils/jobOverlapsRange";

// --- Interfaces ---
interface Props {
  referenceDate?: Date;
  jobs?: any[];
  onSelectDay: (day: Date) => void;
  selectedDay: Date | null;
  selectedJob?: string;
  timeZone?: string;
}

const WEEK_DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const CalendarJobs: React.FC<Props> = ({ referenceDate = new Date(), jobs = [], onSelectDay, selectedDay, selectedJob = "all", timeZone }) => {
  const { ref, isLargeSlot } = useWidgetVariant(500);

  const monthStart = useMemo(() => startOfMonth(referenceDate), [referenceDate]);
  const monthEnd = useMemo(() => endOfMonth(monthStart), [monthStart]);

  const days = useMemo(() => {
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({
      start: calendarStart,
      end: calendarEnd,
    });
  }, [monthStart, monthEnd]);

  const jobsByDate = useMemo(() => {
    return days.reduce<Record<string, { residential: number; commercial: number; jobs: any[] }>>((acc, day) => {
      const dateKey = format(day, "yyyy-MM-dd");
      const dayJobs = jobs.filter(job => jobOverlapsDay(job, day, timeZone));
      acc[dateKey] = {
        residential: dayJobs.filter(job => job.type === "residential").length,
        commercial: dayJobs.filter(job => job.type === "commercial").length,
        jobs: dayJobs,
      };
      return acc;
    }, {});
  }, [jobs, days, timeZone]);

  const currentFilter = selectedJob.toLowerCase();

  const getDayTooltipTitle = (jobsInDay: any[], day: Date) => {
    if (!jobsInDay.length) {
      return (
        <Box>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 800 }}>{format(day, "MMM d")}</Typography>
          <Typography sx={{ fontSize: "0.72rem", opacity: 0.85 }}>No jobs scheduled</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ maxWidth: 260 }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 900, mb: 0.5, color: "#f68c1c" }}>
          {jobsInDay.length} {jobsInDay.length === 1 ? "job" : "jobs"} on {format(day, "MMM d")}
        </Typography>

        {jobsInDay.slice(0, 6).map((job) => (
          <Box
            key={job.id || job.eventUid || `${job.title}-${job.date}`}
            sx={{
              display: "flex",
              gap: 0.75,
              alignItems: "flex-start",
              mb: 0.4,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                mt: "6px",
                flexShrink: 0,
                bgcolor: job.type === "commercial" ? colors.info.base : colors.orange.base,
              }}
            />

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  lineHeight: 1.2,
                }}
                noWrap
              >
                {job.title || job.alias || `Job #${job.jobId || job.id}`}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.68rem",
                  opacity: 0.8,
                  lineHeight: 1.2,
                  textTransform: "capitalize",
                }}
              >
                {job.type || "job"}
              </Typography>
            </Box>
          </Box>
        ))}

        {jobsInDay.length > 6 && <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, mt: 0.5 }}>+{jobsInDay.length - 6} more</Typography>}
      </Box>
    );
  };

  return (
    <Box
      ref={ref}
      sx={{
        width: "100%",
        height: isLargeSlot ? "100%" : "auto",
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        borderRadius: "12px",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          color: colors.orange.base,
          fontWeight: "bold",
          mb: 0.5,
          textAlign: "center",
          fontSize: isLargeSlot ? "1rem" : "0.9rem",
        }}
      >
        {format(referenceDate, "MMMM yyyy", { locale: enUS })}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          mb: 0.5,
          overflow: "hidden",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        }}
      >
        {WEEK_DAYS.map((day) => (
          <Box key={day} sx={{ bgcolor: colors.orange[50], py: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                py: 0.5,
                color: colors.orange.base,
                fontWeight: 800,
                textAlign: "center",
                textTransform: "uppercase",
                fontSize: isLargeSlot ? "0.65rem" : "0.6rem",
              }}
            >
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: isLargeSlot ? 0.5 : 0.75,
          px: isLargeSlot ? 1 : 0,
        }}
      >
        {days.map((day) => {
          const dateKey = format(day, "yyyy-MM-dd");

          const isCurrentMonth = isSameMonth(day, monthStart);
          const isCurrentDay = isToday(day);
          const isSelected = selectedDay ? isSameDay(day, selectedDay) : false;

          const residentialCount = jobsByDate[dateKey]?.residential ?? 0;
          const commercialCount = jobsByDate[dateKey]?.commercial ?? 0;
          const jobsInDay = jobsByDate[dateKey]?.jobs ?? [];

          return (
            <Box
              key={dateKey}
              sx={{
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Tooltip
                title={getDayTooltipTitle(jobsInDay, day)}
                arrow
                placement="top"
                enterDelay={250}
                disableHoverListener={!isCurrentMonth}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                      color: "var(--fx-surface-textsecondary, #64748B)",
                      borderRadius: "12px",
                      p: 1.2,
                      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.22)",
                    },
                  },
                  arrow: {
                    sx: {
                      color: "var(--fx-surface-text, #0F172A)",
                    },
                  },
                }}
              >
                <Paper
                  elevation={0}
                  onClick={() => isCurrentMonth && onSelectDay(day)}
                  sx={{
                    height: isLargeSlot ? 48 : 42,
                    minHeight: isLargeSlot ? 48 : 42,
                    aspectRatio: "unset",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isCurrentMonth ? "pointer" : "default",
                    borderRadius: "12px",

                    // ─── MEJORA VISUAL DE SELECCIÓN Y BORDES ───
                    backgroundColor: isSelected ? colors.orange[50] : "transparent",
                    border: isSelected ? `2px solid ${colors.orange.base}` : isCurrentDay ? `1px dashed ${colors.orange.base}` : "1px solid var(--fx-surface-border, #E2E8F0)",

                    // Si está seleccionado o pertenece al mes actual, opacidad total (1).
                    opacity: isSelected ? 1 : isCurrentMonth ? 1 : 0.3,

                    position: "relative",
                    width: isLargeSlot ? "65%" : "100%",
                    transform: isSelected ? "scale(1.02)" : "none",
                    transition: "all 0.15s ease-in-out",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      // El número se destaca en negrita si está seleccionado o si es el día de hoy
                      color: isSelected ? colors.orange.base : isCurrentDay ? colors.orange.base : "#475569",
                      fontWeight: isCurrentDay || isSelected ? "800" : "500",
                      fontSize: isLargeSlot ? "0.8rem" : "0.8rem",
                    }}
                  >
                    {format(day, "d")}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                    {(currentFilter === "all" || currentFilter === "residential") && residentialCount > 0 && (
                      <ItemWeek mini={!isLargeSlot} color={colors.orange.base} count={residentialCount} />
                    )}
                    {(currentFilter === "all" || currentFilter === "commercial") && commercialCount > 0 && (
                      <ItemWeek mini={!isLargeSlot} color={colors.info.base} count={commercialCount} />
                    )}
                  </Box>
                </Paper>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CalendarJobs;
